<template>
  <div id="main-page">
    <div class="container">
      <app-bar
        title="Detail Event"
        previous-link="/event" />

      <Loading v-if="loading.getEventDetail" />

      <template v-else-if="detailEvent">
        <!-- Banner Image -->
        <div class="event-banner">
          <img :src="detailEvent.gambar_url" :alt="detailEvent.judul" />
          <span class="badge-harga" :class="detailEvent.is_gratis === 't' ? 'badge-gratis' : 'badge-bayar'">
            {{ detailEvent.is_gratis === 't' ? 'GRATIS' : formatHarga(detailEvent.harga) }}
          </span>
        </div>

        <!-- Event Info Card -->
        <div class="section-card mt-3">
          <h4 class="fw-700 mb-3">{{ detailEvent.judul }}</h4>

          <!-- Same-day: single row with start date + bullet + end time -->
          <div class="event-meta-row mb-2" v-if="detailEvent.tgl_mulai && isSameDay(detailEvent.tgl_mulai, detailEvent.tgl_selesai)">
            <div class="meta-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#007a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            </div>
            <div>
              <div class="meta-label">Tanggal</div>
              <div class="meta-value">
                {{ formatDate(detailEvent.tgl_mulai) }} · {{ formatTime(detailEvent.tgl_mulai) }}
                <template v-if="detailEvent.tgl_selesai"> – {{ formatTime(detailEvent.tgl_selesai) }}</template>
                WIB
              </div>
            </div>
          </div>

          <!-- Different days: two separate rows -->
          <template v-else-if="detailEvent.tgl_mulai">
            <div class="event-meta-row mb-2">
              <div class="meta-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#007a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              <div>
                <div class="meta-label">Mulai</div>
                <div class="meta-value">{{ formatTanggal(detailEvent.tgl_mulai) }}</div>
              </div>
            </div>
            <div class="event-meta-row mb-2" v-if="detailEvent.tgl_selesai">
              <div class="meta-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#007a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              <div>
                <div class="meta-label">Selesai</div>
                <div class="meta-value">{{ formatTanggal(detailEvent.tgl_selesai) }}</div>
              </div>
            </div>
          </template>

          <div class="event-meta-row mb-2" v-if="detailEvent.lokasi">
            <div class="meta-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#007a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </div>
            <div>
              <div class="meta-label">Lokasi</div>
              <div class="meta-value">{{ detailEvent.lokasi }}</div>
            </div>
          </div>

          <div class="event-meta-row" v-if="detailEvent.kuota">
            <div class="meta-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#007a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <div>
              <div class="meta-label">Kuota</div>
              <div class="meta-value">{{ detailEvent.kuota }} peserta</div>
            </div>
          </div>
        </div>

        <!-- Deskripsi Card -->
        <div class="section-card mt-3" v-if="detailEvent.deskripsi">
          <h6 class="section-title fw-700">Deskripsi</h6>
          <p class="section-body text-muted mb-0">{{ detailEvent.deskripsi }}</p>
        </div>

        <!-- Booth List Card -->
        <div class="section-card mt-3" v-if="detailBooth.length">
          <h6 class="section-title fw-700">Booth Tersedia</h6>
          <div class="booth-list">
            <div class="booth-item" v-for="booth in detailBooth" :key="booth.id">
              <div class="booth-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#007a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              </div>
              <span>{{ booth.nama }}</span>
            </div>
          </div>
        </div>

        <!-- Daftar Button -->
        <div class="pt-3 pb-4">
          <button class="btn btn-primary w-100 py-2 fw-600 fs-16 btn-daftar" @click="handleDaftar">
            Daftar Event
          </button>
        </div>

        <!-- Modal Kode Undangan -->
        <div class="modal fade" id="modal-kode-undangan" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header border-0 pb-0">
                <h6 class="modal-title fw-700">Kode Undangan</h6>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body pt-2">
                <p class="text-muted fs-13 mb-3">Masukkan kode undangan untuk mendaftar event ini.</p>
                <input
                  v-model="kodeUndangan"
                  type="text"
                  class="form-control"
                  placeholder="Kode undangan"
                  @keyup.enter="submitKodeUndangan" />
              </div>
              <div class="modal-footer border-0 pt-0">
                <button
                  class="btn btn-primary w-100 py-2 fw-600 fs-15"
                  :disabled="!kodeUndangan"
                  @click="submitKodeUndangan">
                  Lanjut
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Pilih Peserta -->
        <div class="modal fade" id="modal-daftar-event" tabindex="-1" aria-hidden="true">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header border-0 pb-0">
                <h6 class="modal-title fw-700">Pilih Peserta</h6>
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
              </div>
              <div class="modal-body pt-2">
                <p class="text-muted fs-13 mb-3">Pilih anggota yang akan mendaftarkan diri ke event ini.</p>

                <div v-if="loading.getPesertaEvent" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                </div>

                <div v-else class="peserta-list">
                  <div
                    v-for="peserta in eventPeserta"
                    :key="peserta.pas_id"
                    class="peserta-item"
                    :class="{ active: selectedPeserta && selectedPeserta.pas_id === peserta.pas_id }"
                    @click="selectedPeserta = peserta">
                    <div class="peserta-avatar" :style="avatarStyle(peserta.nama_peserta)">
                      {{ initials(peserta.nama_peserta) }}
                    </div>
                    <div class="peserta-info">
                      <div class="peserta-name">{{ peserta.nama_peserta }}</div>
                      <div class="peserta-hubungan">{{ peserta.hubungan }}</div>
                    </div>
                    <div v-if="selectedPeserta && selectedPeserta.pas_id === peserta.pas_id" class="peserta-check">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button
                  class="btn btn-primary w-100 py-2 fw-600 fs-15"
                  :disabled="!selectedPeserta || loading.daftarEvent"
                  @click="submitDaftar">
                  <span v-if="loading.daftarEvent" class="spinner-border spinner-border-sm me-2" role="status"></span>
                  Konfirmasi Pendaftaran
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Other Events -->
        <div class="other-events pb-4" v-if="otherEvents.length">
          <h6 class="section-title fw-700 mb-3">Event Lainnya</h6>
          <div class="row flex-nowrap overflow-auto pb-2">
            <div v-for="event in otherEvents" :key="event.id" class="col-8 col-md-4 pt-2 pb-2">
              <div class="card border-0 shadow" @click="goToEvent(event)">
                <div class="card-body p-0">
                  <div class="d-flex flex-column">
                    <div class="img-wrapper">
                      <img class="card-img" :src="event.gambar_url">
                      <span class="badge-harga" :class="event.is_gratis === 't' ? 'badge-gratis' : 'badge-bayar'">
                        {{ event.label_harga }}
                      </span>
                      <span v-if="event.label_sisa_kuota" class="badge-kuota">
                        {{ event.label_sisa_kuota }}
                      </span>
                    </div>
                    <div class="pt-2 pb-3 px-3">
                      <h5 class="card-title fw-700">{{ event.judul }}</h5>
                      <div class="event-meta text-muted fs-13 mt-1" v-if="event.tgl_mulai">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        {{ formatTanggal(event.tgl_mulai) }}
                      </div>
                      <div class="event-meta text-muted fs-13 mt-1" v-if="event.lokasi">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {{ event.lokasi }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty State -->
      <div
        v-else
        class="not-found d-flex flex-column text-center pb-5 pt-5">
        <div class="bd-highlight">
          <img src="@/assets/icon/not-found.png" alt="">
        </div>
        <div class="bd-highlight">
          <p class="text-title-active fm-poppins fst-normal fw-600 fs-24 lh-36">Tidak Ada Hasil</p>
        </div>
        <div class="bd-highlight">
          <p class="text-body-light fm-nunito fst-normal fw-400 fs-16 lh-21">Detail event tidak ditemukan.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import Loading from '@/components/utility/Loading'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Modal } from 'bootstrap'
import { title } from '@/core/page'
import { getEventDetail, detailEvent, detailBooth, loading, getEvent, listEvent, getPesertaEvent, eventPeserta, daftarEvent } from '@/core/event'
import { showNotif } from '@/core/utility'
import { token } from '@/core/auth'

const AVATAR_COLORS = [
  ['#007A98', '#E0F7FA'],
  ['#1CB242', '#E8F5E9'],
  ['#7B5EA7', '#EDE7F6'],
  ['#E67E22', '#FFF3E0'],
  ['#E53935', '#FFEBEE'],
  ['#0288D1', '#E1F5FE'],
  ['#00897B', '#E0F2F1']
]

export default {
  components: {
    AppBar, Loading
  },
  setup () {
    const route = useRoute()
    const router = useRouter()

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']

    const selectedPeserta = ref(null)
    const kodeUndangan = ref('')

    const initials = (name) => {
      if (!name) return '?'
      return name.trim().split(/\s+/).slice(0, 2).map(w => w[0].toUpperCase()).join('')
    }

    const avatarStyle = (name) => {
      if (!name) return {}
      const idx = name.charCodeAt(0) % AVATAR_COLORS.length
      const [color, bg] = AVATAR_COLORS[idx]
      return { background: bg, color }
    }

    const parseDate = (tgl) => new Date(tgl.replace(' ', 'T'))

    const formatDate = (tgl) => {
      if (!tgl) return ''
      const date = parseDate(tgl)
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
    }

    const formatTime = (tgl) => {
      if (!tgl) return ''
      const date = parseDate(tgl)
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    }

    const formatTanggal = (tgl) => {
      if (!tgl) return ''
      return `${formatDate(tgl)} · ${formatTime(tgl)} WIB`
    }

    const isSameDay = (tgl1, tgl2) => {
      if (!tgl1 || !tgl2) return false
      const d1 = parseDate(tgl1)
      const d2 = parseDate(tgl2)
      return d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
    }

    const formatHarga = (harga) => {
      if (!harga || harga === '0') return 'GRATIS'
      return 'Rp ' + Number(harga).toLocaleString('id-ID')
    }

    const otherEvents = computed(() => {
      const currentId = route.params.id
      return listEvent.value.filter(e => String(e.id) !== String(currentId))
    })

    const goToEvent = (event) => {
      router.push({
        name: 'EventDetail',
        params: { id: event.id }
      })
    }

    const handleDaftar = () => {
      if (!token.value) {
        showNotif({ status: 'warning', message: 'Silakan masuk terlebih dahulu untuk mendaftar event.' })
        router.push({ name: 'Login' })
        return
      }
      selectedPeserta.value = eventPeserta.value.length > 0 ? eventPeserta.value[0] : null
      const modal = new Modal(document.getElementById('modal-daftar-event'))
      modal.show()
    }

    const doSubmitDaftar = async () => {
      const result = await daftarEvent(detailEvent.value.id, selectedPeserta.value.pas_id, kodeUndangan.value || null)
      if (!result.status) return

      const modal = Modal.getInstance(document.getElementById('modal-daftar-event'))
      if (modal) modal.hide()

      if (!result.data.midtrans) {
        showNotif({ status: 'success', message: result.message || 'Pendaftaran berhasil' })
        router.push({
          name: 'MyEventDetail',
          query: {
            acara_id: result.data.hai_acara_id,
            pas_id_periksa: result.data.pas_id_periksa
          }
        })
        return
      }

      window.getSnap().then(snap => {
        snap.pay(result.data.midtrans.token, {
          onSuccess: () => {
            router.push({
              name: 'MyEventDetail',
              query: {
                acara_id: result.data.hai_acara_id,
                pas_id_periksa: result.data.pas_id_periksa
              }
            })
          }
        })
      })
    }

    const submitDaftar = () => {
      if (!selectedPeserta.value) return
      if (detailEvent.value.jenis_event === 'undangan') {
        kodeUndangan.value = ''
        const pesertaModal = Modal.getInstance(document.getElementById('modal-daftar-event'))
        if (pesertaModal) pesertaModal.hide()
        const undanganModal = new Modal(document.getElementById('modal-kode-undangan'))
        undanganModal.show()
        return
      }
      doSubmitDaftar()
    }

    const submitKodeUndangan = () => {
      if (!kodeUndangan.value) return
      const undanganModal = Modal.getInstance(document.getElementById('modal-kode-undangan'))
      if (undanganModal) undanganModal.hide()
      doSubmitDaftar()
    }

    onMounted(async () => {
      const id = route.params.id
      await Promise.all([
        getEventDetail(id),
        listEvent.value.length === 0 ? getEvent() : Promise.resolve(),
        (token.value && eventPeserta.value.length === 0) ? getPesertaEvent() : Promise.resolve()
      ])

      nextTick(() => {
        title.value = 'Detail Event'
      })
    })

    return {
      loading,
      detailEvent,
      detailBooth,
      listEvent,
      otherEvents,
      eventPeserta,
      selectedPeserta,
      initials,
      avatarStyle,
      formatDate,
      formatTime,
      formatTanggal,
      formatHarga,
      isSameDay,
      goToEvent,
      kodeUndangan,
      handleDaftar,
      submitKodeUndangan,
      submitDaftar
    }
  }
}
</script>

<style lang="scss" scoped>
.event-banner {
  position: relative;
  margin: 0 -12px;

  img {
    width: 100%;
    display: block;
    object-fit: contain;
    height: auto;
  }

  .badge-harga {
    position: absolute;
    top: 12px;
    left: 12px;
    font-size: 12px;
    font-weight: 600;
    padding: 5px 14px;
    border-radius: 20px;
    line-height: 1.4;

    &.badge-gratis {
      background-color: #198754;
      color: #fff;
    }

    &.badge-bayar {
      background-color: #fff;
      color: var(--color-primary, #007a98);
      box-shadow: 0 1px 4px rgba(0,0,0,0.15);
    }
  }
}

.section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.08);
  padding: 16px;

  h4 {
    font-size: 20px;
    line-height: 28px;
    color: #1A202C;
  }
}

.event-meta-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  .meta-icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    background-color: #E6F4F8;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .meta-label {
    font-size: 12px;
    color: #6c757d;
    line-height: 1.4;
  }

  .meta-value {
    font-size: 14px;
    font-weight: 600;
    color: #1A202C;
    line-height: 1.5;
  }
}

.section-title {
  font-size: 16px;
  color: #1A202C;
  margin-bottom: 10px;
}

.section-body {
  font-size: 14px;
  line-height: 1.7;
  white-space: pre-line;
}

.booth-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.booth-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background-color: #F7FAFC;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #1A202C;

  .booth-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    background-color: #E6F4F8;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.btn-daftar {
  border-radius: 10px;
  background-color: #007a98;
  border-color: #007a98;

  &:hover, &:active {
    background-color: darken(#007a98, 8%);
    border-color: darken(#007a98, 8%);
  }
}

.other-events {
  border-top: 1px solid #EFF0F7;
  padding-top: 16px;

  .card {
    border-radius: 10px;
    cursor: pointer;
    height: 100%;

    .card-title {
      font-size: 16px;
      line-height: 20px;
      margin-bottom: 2px;
    }
  }

  .card-img {
    width: 100%;
    height: 180px;
    display: block;
    object-fit: contain;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }

  .img-wrapper {
    position: relative;

    .badge-harga {
      position: absolute;
      top: 8px;
      left: 8px;
      font-size: 11px;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 20px;
      line-height: 1.4;

      &.badge-gratis {
        background-color: #198754;
        color: #fff;
      }

      &.badge-bayar {
        background-color: #fff;
        color: var(--color-primary, #007a98);
        box-shadow: 0 1px 4px rgba(0,0,0,0.15);
      }
    }

    .badge-kuota {
      position: absolute;
      top: 8px;
      right: 8px;
      font-size: 11px;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 20px;
      line-height: 1.4;
      background-color: #fff3cd;
      color: #856404;
      box-shadow: 0 1px 4px rgba(0,0,0,0.15);
    }
  }

  .event-meta {
    display: flex;
    align-items: center;
    gap: 5px;

    svg {
      flex-shrink: 0;
    }
  }
}

.peserta-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.peserta-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 2px solid transparent;
  background-color: #F7FAFC;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;

  &.active {
    border-color: #007a98;
    background-color: #E6F4F8;
  }

  .peserta-avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .peserta-info {
    flex: 1;
    min-width: 0;

    .peserta-name {
      font-size: 14px;
      font-weight: 600;
      color: #1A202C;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .peserta-hubungan {
      font-size: 12px;
      color: #6c757d;
    }
  }

  .peserta-check {
    color: #007a98;
    flex-shrink: 0;
  }
}
</style>
